import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "./ui/button";
import { trackCTAClick } from "@/lib/analytics";
import logoAsset from "@/assets/circlo-logo.png.asset.json";

const logoImage = logoAsset.url;

const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { name: "Work", path: `/work` },
    { name: "How we work", path: `/how-we-work` },
    { name: "About", path: `/about` },
  ];

  const isActive = (path: string) => location.pathname === path;
  const homeLink = '/';

  return (
    <header className="relative z-[100] w-full bg-background/95 backdrop-blur-sm border-b border-border">
      <nav className="container mx-auto px-4 h-20 flex items-center justify-between">
        <Link to={homeLink} className="flex items-center" aria-label="Circlo home">
          <img
            src={logoImage}
            alt="Circlo"
            className="h-9 md:h-11 w-auto object-contain"
            width={682}
            height={256}
          />
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-2">
          {navItems.map((item) => (
            <Link
              key={item.name}
              to={item.path}
              className={`group relative px-3 py-2 text-base xl:text-lg font-semibold rounded-md transition-all duration-300 ease-out hover:-translate-y-0.5 focus-visible:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                isActive(item.path) ? "text-primary" : "text-foreground hover:text-primary"
              }`}
            >
              <span className="relative inline-block">
                {item.name}
                <span
                  aria-hidden="true"
                  className={`pointer-events-none absolute -bottom-1 left-0 h-[2px] bg-primary transition-transform duration-300 ease-out origin-left ${
                    isActive(item.path)
                      ? "w-full scale-x-100"
                      : "w-full scale-x-0 group-hover:scale-x-100 group-focus-visible:scale-x-100"
                  }`}
                />
              </span>
            </Link>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <Button
            asChild
            size="lg"
           
            onClick={() => trackCTAClick('start_project_nav', location.pathname)}
          >
            <Link to={`/contact`}>Start a project</Link>
          </Button>
        </div>

        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="lg:hidden p-2 text-foreground hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary rounded"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {isMobileMenuOpen && (
        <div
          className="lg:hidden absolute left-0 right-0 top-full bg-background z-[99] overflow-y-auto"
          style={{ height: 'calc(100vh - 5rem)' }}
        >
          <div className="container mx-auto px-4 py-6">
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.name} className="border-b border-border">
                  <Link
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block py-4 text-base font-medium ${
                      isActive(item.path) ? "text-primary" : "text-foreground hover:text-primary"
                    }`}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-8 sticky bottom-0 bg-background pt-4">
              <Button
                asChild
                className="w-full"
                onClick={() => {
                  trackCTAClick('start_project_nav_mobile', location.pathname);
                  setIsMobileMenuOpen(false);
                }}
              >
                <Link to={`/contact`}>Start a project</Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;